import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context first, before any usage
const AuthContext = createContext(null);

// Hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simple login function that accepts any credentials
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      // Create a user object from the provided email
      const user = {
        uid: Date.now().toString(), // Generate a unique ID
        email: email,
        name: email?.split('@')[0] || 'User'
      };
      
      setCurrentUser(user);
      // Store user in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(user));
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setCurrentUser(null);
      localStorage.removeItem('user');
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      return false;
    }
  };

  // Check for stored user on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const value = {
    currentUser,
    login,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 